"use client";

import {
  useEffect,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { ArrowRight, Check } from "lucide-react";
import { Typography } from "@/components/ui";

const STEP_DURATION_MS = 3200;
const PUSH_FILL_MS = 2600;

export interface PlatformFlowStep {
  step: string;
  title: string;
  description: string;
}

export interface PlatformFlowGroup {
  name: string;
  /** Omit when the page has no real count to show. */
  screens?: number;
}

/** Labels for the push-progress plate under step 01, one per status. */
export interface PlatformFlowPushLabels {
  pending: string;
  active: string;
  done: string;
}

const retailSteps: PlatformFlowStep[] = [
  {
    step: "01",
    title: "Pickcel Cloud Dashboard",
    description: "One control centre for every screen you own.",
  },
  {
    step: "02",
    title: "Store Groups",
    description: "Organise screens by store, region, concept, or campaign.",
  },
  {
    step: "03",
    title: "In-Store Displays",
    description: "Windows, shelves, kiosks, POS, and menu boards update together.",
  },
];

const retailGroups: PlatformFlowGroup[] = [
  { name: "Downtown Flagship", screens: 24 },
  { name: "Airport Store", screens: 18 },
];

const retailLiveLabels = [
  "Pushing content from HQ to your stores…",
  "Content synced across store groups…",
  "Live on all screens across your network",
];

const retailPushLabels: PlatformFlowPushLabels = {
  pending: "Ready to push",
  active: "Pushing update to all stores…",
  done: "Pushed to all stores",
};

interface PlatformFlowProps {
  steps?: PlatformFlowStep[];
  groups?: PlatformFlowGroup[];
  /** Status-line copy shown in the footer, one per step. */
  liveLabels?: string[];
  pushLabels?: PlatformFlowPushLabels;
  ariaLabel?: string;
}

function MonitorGlyph({
  className = "",
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 64 58"
      fill="none"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <rect x="2" y="2" width="60" height="42" rx="5" fill="currentColor" opacity="0.1" />
      <rect
        x="2"
        y="2"
        width="60"
        height="42"
        rx="5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        opacity="0.7"
      />
      <path
        d="M32 44v7M22 54h20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.7"
      />
    </svg>
  );
}

function FlowArrow({
  className = "",
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`pointer-events-none absolute top-12 bottom-12 z-20 hidden w-9 -translate-x-1/2 md:block ${className}`}
      style={style}
      aria-hidden="true"
    >
      <span className="absolute inset-y-0 left-1/2 w-0 -translate-x-1/2 border-l border-dashed border-primary/20" />
      <span className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-primary/15 bg-white text-primary shadow-[0_4px_14px_rgba(37,56,148,0.14)] ring-4 ring-white">
        <ArrowRight className="h-3.5 w-3.5 stroke-[2.5]" />
      </span>
    </div>
  );
}

function UsersGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3ZM8 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3ZM8.5 13.5c-2.485 0-4.5 1.343-4.5 3V18h9v-1.5c0-1.657-2.015-3-4.5-3ZM15.5 13.5c-.35 0-.69.03-1.02.09 1.21.7 2.02 1.8 2.02 3.06V18h5v-1.5c0-1.657-2.015-3-4.5-3Z"
        fill="currentColor"
      />
    </svg>
  );
}

function LiveDot({ className = "" }: { className?: string }) {
  return (
    <span className={`relative flex h-2 w-2 shrink-0 ${className}`}>
      <span className="motion-reduce:hidden absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]" />
    </span>
  );
}

function PreviewPlate({
  children,
  className = "",
  ...props
}: {
  children: ReactNode;
  className?: string;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`mt-7 flex min-h-[80px] flex-col justify-center rounded-xl border border-primary/10 bg-gradient-to-br from-primary/[0.03] to-white px-4 py-3 shadow-[0_2px_12px_rgba(37,56,148,0.06)] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function PlatformFlow({
  steps = retailSteps,
  groups = retailGroups,
  liveLabels = retailLiveLabels,
  pushLabels = retailPushLabels,
  ariaLabel = "How Pickcel connects the dashboard to in-store displays in real time",
}: PlatformFlowProps = {}) {
  const [activeStep, setActiveStep] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [pushFilled, setPushFilled] = useState(false);

  const columnCount = steps.length;
  // Full class strings so Tailwind keeps md:grid-cols-4 in the CSS build.
  const gridColsClass =
    columnCount >= 4
      ? "md:grid-cols-4"
      : columnCount === 2
        ? "md:grid-cols-2"
        : "md:grid-cols-3";
  const stepPadClass = columnCount >= 4 ? "px-5 py-9 md:px-6 md:py-10" : "px-8 py-10 md:px-10 md:py-12";

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      setActiveStep(prev => {
        if (prev >= steps.length - 1) {
          setCycle(c => c + 1);
          return 0;
        }
        return prev + 1;
      });
    }, STEP_DURATION_MS);
    return () => clearInterval(id);
  }, [steps.length]);

  useEffect(() => {
    if (activeStep === 0) {
      setPushFilled(false);
      const id = window.setTimeout(() => setPushFilled(true), 40);
      return () => window.clearTimeout(id);
    }
    setPushFilled(true);
  }, [activeStep, cycle]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/15 bg-white shadow-[0_8px_30px_rgba(37,56,148,0.06)]">
      <div
        className={`relative grid grid-cols-1 ${gridColsClass}`}
        role="img"
        aria-label={ariaLabel}
      >
        {steps.map((item, index) => {
          const status = index < activeStep ? "done" : index === activeStep ? "active" : "pending";
          const isActive = status === "active";
          const isDone = status === "done";
          const isPending = status === "pending";
          const showMonitorPlate = index === 2;
          const isFourthStep = index === 3;

          return (
            <div
              key={item.step}
              className={`relative flex flex-col transition-all duration-500 ${stepPadClass} ${
                index < steps.length - 1 ? "border-b border-primary/10 md:border-b-0" : ""
              } ${isActive ? "bg-primary/[0.025]" : ""} ${isPending ? "opacity-75" : "opacity-100"}`}
            >
              {isActive ? (
                <span
                  className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                  aria-hidden="true"
                />
              ) : null}

              <span
                className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-sm font-bold tracking-[0.14em] transition-all duration-500 ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : isDone
                      ? "bg-green-500/10 text-green-600"
                      : "bg-primary/5 text-primary/30"
                }`}
                aria-hidden="true"
              >
                {item.step}
              </span>

              <Typography
                variant="h5"
                as="h3"
                className={`mt-4 mb-2 font-semibold leading-snug transition-colors duration-500 ${
                  isPending ? "text-text-secondary" : "text-text-primary"
                }`}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body"
                as="p"
                className="min-h-[2.75rem] text-sm leading-relaxed text-text-secondary"
              >
                {item.description}
              </Typography>

              {index === 0 && (
                <PreviewPlate aria-hidden="true">
                  <div className="mb-2.5 flex items-center justify-between text-[11px] font-medium">
                    <span
                      className={`flex items-center gap-1.5 transition-colors duration-500 ${
                        isDone ? "text-text-primary" : "text-text-secondary"
                      }`}
                    >
                      {isDone && <Check className="h-3.5 w-3.5 shrink-0 text-green-600" aria-hidden="true" />}
                      {isPending && pushLabels.pending}
                      {isActive && pushLabels.active}
                      {isDone && pushLabels.done}
                    </span>
                    <span
                      className={`tabular-nums font-semibold transition-colors duration-500 ${
                        isDone ? "text-green-600" : isActive ? "text-primary" : "text-text-secondary/50"
                      }`}
                    >
                      {isPending || (isActive && !pushFilled) ? "0%" : "100%"}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-primary/10">
                    <div
                      className={`h-full rounded-full ease-out ${
                        pushFilled ? "w-full" : "w-0"
                      } ${isDone ? "bg-green-500" : "bg-primary"}`}
                      style={{
                        transitionProperty: "width, background-color",
                        transitionDuration: isActive ? `${PUSH_FILL_MS}ms` : "400ms",
                      }}
                    />
                  </div>
                </PreviewPlate>
              )}

              {index === 1 && (
                <PreviewPlate className="py-2" aria-hidden="true">
                  <ul>
                    {groups.map((store, storeIndex) => (
                      <li
                        key={store.name}
                        className={`flex items-center justify-between gap-2 py-2 text-xs font-medium text-text-primary ${
                          storeIndex < groups.length - 1 ? "border-b border-primary/10" : ""
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {isDone ? (
                            <Check className="h-3 w-3 shrink-0 text-green-600" aria-hidden="true" />
                          ) : (
                            <span
                              className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-500 ${
                                isPending ? "bg-primary/25" : "bg-primary"
                              } ${isActive ? "animate-pulse" : ""}`}
                            />
                          )}
                          {store.name}
                        </span>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors duration-500 ${
                            isDone
                              ? "bg-green-500/12 text-green-700"
                              : isActive
                                ? "bg-primary/12 text-primary"
                                : "bg-primary/5 text-primary/40"
                          }`}
                        >
                          {isDone ? "Synced" : store.screens != null ? `${store.screens} Screens` : "Screen group"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </PreviewPlate>
              )}

              {showMonitorPlate && (
                <PreviewPlate className="justify-end bg-gradient-to-br from-primary/[0.04] to-primary/[0.01]" aria-hidden="true">
                  <div className="flex items-end gap-3">
                    <MonitorGlyph
                      className={`w-[68px] shrink-0 transition-colors duration-500 ${
                        isPending ? "text-primary/30" : isDone ? "text-green-600" : "text-primary"
                      }`}
                    />
                    <div className="flex items-end gap-2.5">
                      {[0, 1, 2].map(i => (
                        <MonitorGlyph
                          key={i}
                          className={`w-8 shrink-0 transition-all duration-500 ${
                            isPending
                              ? "text-primary/30 opacity-40"
                              : isDone
                                ? "text-green-600 opacity-100"
                                : "text-primary opacity-100"
                          }`}
                          style={{
                            transitionDelay: isActive || isDone ? `${i * 140}ms` : "0ms",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </PreviewPlate>
              )}

              {isFourthStep && (
                <PreviewPlate aria-hidden="true">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-500 ${
                        isDone
                          ? "bg-green-500/12 text-green-600"
                          : isActive
                            ? "bg-primary/12 text-primary"
                            : "bg-primary/5 text-primary/40"
                      }`}
                    >
                      <UsersGlyph
                        className={`h-4 w-4 transition-colors duration-500 ${
                          isPending ? "text-primary/40" : isDone ? "text-green-600" : "text-primary"
                        }`}
                      />
                    </span>
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-semibold transition-colors duration-500 ${
                          isPending ? "text-text-secondary" : "text-text-primary"
                        }`}
                      >
                        {isDone ? "Audience reached" : isActive ? "Reaching customers & staff…" : "Ready for viewers"}
                      </p>
                      <p className="text-[11px] text-text-secondary">Live on the floor</p>
                    </div>
                  </div>
                </PreviewPlate>
              )}
            </div>
          );
        })}

        {Array.from({ length: Math.max(columnCount - 1, 0) }, (_, i) => (
          <FlowArrow
            key={`arrow-${i}`}
            className=""
            style={{ left: `${((i + 1) / columnCount) * 100}%` }}
          />
        ))}
      </div>

      <div className="flex items-center justify-center gap-2.5 border-t border-primary/10 bg-gradient-to-r from-green-500/[0.04] via-white to-green-500/[0.04] px-8 py-3.5 md:px-10">
        <LiveDot />
        <span className="text-xs font-medium tracking-wide text-text-secondary">
          <span className="font-semibold text-green-600">Live</span>
          {" — "}
          {liveLabels[activeStep] ?? liveLabels[liveLabels.length - 1]}
        </span>
      </div>
    </div>
  );
}
